import {
  faEye,
  faEyeSlash,
  IconDefinition,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Dispatch,
  HTMLInputTypeAttribute,
  SetStateAction,
  useState,
} from 'react';
import { cn } from '../../lib/utils';

export type InputProps = React.ComponentProps<'input'> & {
  startIcon?: IconDefinition;
  endIcon?: IconDefinition;
  showPassword?: boolean;
};

function PasswordIcon({
  showPassword,
  type,
  isPasswordVisible,
  setIsPasswordVisible,
}: {
  showPassword: boolean;
  type: HTMLInputTypeAttribute;
  isPasswordVisible: boolean;
  setIsPasswordVisible: Dispatch<SetStateAction<boolean>>;
}) {
  return showPassword && type === 'password' ? (
    <FontAwesomeIcon
      className={cn(
        'text-neutrals-700 group-focus-within:text-primary-blue-600 transform transition-colors',
        'absolute right-3 top-1/2 -translate-y-1/2',
      )}
      icon={isPasswordVisible ? faEyeSlash : faEye}
      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
    />
  ) : (
    <></>
  );
}

// eslint-disable-next-line max-lines-per-function
function Input({
  className,
  type,
  startIcon,
  endIcon,
  showPassword,
  ...props
}: InputProps) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  function getInputType() {
    return type === 'password'
      ? isPasswordVisible
        ? 'text'
        : 'password'
      : type;
  }

  return (
    <div className="relative group rounded-md bg-transparent">
      {startIcon && (
        <FontAwesomeIcon
          icon={startIcon}
          className={cn(
            'text-neutrals-700 group-focus-within:text-primary-blue-600 transform transition-colors',
            'absolute left-2.5 top-1/2  -translate-y-1/2',
          )}
        />
      )}
      <input
        type={getInputType()}
        className={cn(
          'peer',
          'flex w-full py-2',
          `${startIcon ? 'ps-8' : 'ps-4'}`,
          `${endIcon ? 'pe-9' : 'pe-4'}`,
          'rounded-md border border-neutrals-300',
          'bg-white text-sm transition-colors leading-6',
          'file:border-0 file:bg-transparent file:text-md file:font-medium',
          'focus-visible:outline-hidden focus-visible:border-neutrals-800',
          'disabled:cursor-not-allowed disabled:opacity-70',
          'placeholder:text-neutrals-700',
          className,
        )}
        data-slot="input"
        {...props}
      />
      {endIcon && (
        <FontAwesomeIcon
          className={cn(
            'text-neutrals-700 group-focus-within:text-primary-blue-600 transform transition-colors',
            'absolute right-3 top-1/2 -translate-y-1/2',
          )}
          icon={endIcon}
        />
      )}
      <PasswordIcon
        isPasswordVisible={isPasswordVisible}
        setIsPasswordVisible={setIsPasswordVisible}
        type={type}
        showPassword={showPassword}
      />
    </div>
  );
}

export { Input };
